import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Header } from "@/components/admin/BookingsTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash2, Building2, FileText, Gift, Send, Download, Eye } from "lucide-react";

export default function BusinessTab() {
  const [corps, setCorps] = useState([]);
  const [loyalty, setLoyalty] = useState({ enabled: false, every_n_rides: 10, discount_percent: 50 });
  const [open, setOpen] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(null);
  const [f, setF] = useState({ name: "", email_domain: "", monthly_limit_nzd: 5000, active: true, billing_email: "", admin_password: "" });

  const load = async () => {
    try {
      const [c, l] = await Promise.all([api.get("/corporate"), api.get("/settings/loyalty")]);
      setCorps(c.data); setLoyalty(l.data);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const addCorp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/corporate", { ...f, monthly_limit_nzd: Number(f.monthly_limit_nzd), billing_email: f.billing_email || null, admin_password: f.admin_password || null });
      toast.success(f.admin_password ? "Corporate account added with self-serve login" : "Corporate account added"); setOpen(false);
      setF({ name: "", email_domain: "", monthly_limit_nzd: 5000, active: true, billing_email: "", admin_password: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendInvoice = async (id) => {
    setSendingId(id);
    try {
      const { data } = await api.post(`/corporate/${id}/invoice/send`);
      toast.success(`Invoice emailed to ${data.sent_to} — ${data.trips} trip(s), NZ$ ${data.total_nzd.toFixed(2)}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSendingId(null); }
  };

  const previewInvoice = async (id) => {
    setPreviewLoading(id);
    try {
      const token = localStorage.getItem("apex_token");
      const res = await fetch(`${(await import("@/lib/api")).BACKEND_URL}/api/corporate/${id}/invoice/preview.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Preview failed");
      const blob = await res.blob();
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    finally { setPreviewLoading(null); }
  };
  const closePreview = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); };

  const delCorp = async (id) => {
    if (!window.confirm("Delete this corporate account?")) return;
    try { await api.delete(`/corporate/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); }
  };

  const openInvoice = async (id) => {
    try { const { data } = await api.get(`/corporate/invoices/${id}`); setInvoice(data); }
    catch { toast.error("Failed"); }
  };

  const saveLoyalty = async (patch) => {
    const next = { ...loyalty, ...patch };
    setLoyalty(next);
    try { await api.put("/settings/loyalty", next); toast.success("Loyalty saved"); }
    catch { toast.error("Failed"); }
  };

  return (
    <div>
      <Header title="Business" subtitle="Corporate accounts and loyalty rewards" />

      <div className="glass-card rounded-2xl p-6 gold-border mb-8" data-testid="loyalty-panel">
        <div className="flex items-start justify-between gap-4">
          <div className="flex gap-3">
            <div className="w-11 h-11 rounded-lg bg-amber-400/10 grid place-items-center"><Gift className="w-5 h-5 text-amber-400" /></div>
            <div>
              <div className="font-display text-lg font-bold">Loyalty Rewards</div>
              <p className="text-sm text-slate-400 mt-1">Auto-discount every Nth completed ride per rider account.</p>
            </div>
          </div>
          <Switch checked={loyalty.enabled} onCheckedChange={(v) => saveLoyalty({ enabled: v })} data-testid="loyalty-toggle" />
        </div>
        <div className="mt-5 grid grid-cols-2 gap-4">
          <div><Label className="text-xs uppercase text-slate-400">Every N rides</Label>
            <Input type="number" min="2" value={loyalty.every_n_rides} onChange={(e) => setLoyalty({ ...loyalty, every_n_rides: Number(e.target.value) })} onBlur={() => saveLoyalty({})} className="bg-black/30 border-white/10 h-10 mt-1" data-testid="loyalty-every-n" /></div>
          <div><Label className="text-xs uppercase text-slate-400">Discount %</Label>
            <Input type="number" min="1" max="100" value={loyalty.discount_percent} onChange={(e) => setLoyalty({ ...loyalty, discount_percent: Number(e.target.value) })} onBlur={() => saveLoyalty({})} className="bg-black/30 border-white/10 h-10 mt-1" data-testid="loyalty-discount" /></div>
        </div>
        <p className="text-xs text-slate-500 mt-3 font-mono">Current: {loyalty.enabled ? `Every ${loyalty.every_n_rides}th ride → ${loyalty.discount_percent}% off` : "Disabled"}</p>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold flex items-center gap-2"><Building2 className="w-5 h-5 text-amber-400" /> Corporate Accounts</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-gold rounded-full" data-testid="corp-add"><Plus className="w-4 h-4 mr-2" /> New account</Button></DialogTrigger>
          <DialogContent className="bg-[#111827] border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">Add corporate account</DialogTitle></DialogHeader>
            <form onSubmit={addCorp} className="grid gap-4">
              <div><Label className="text-xs uppercase text-slate-400">Company name</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" data-testid="corp-name" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Email domain</Label><Input required value={f.email_domain} onChange={(e) => setF({ ...f, email_domain: e.target.value })} placeholder="company.co.nz" className="bg-black/30 border-white/10 mt-1 h-10 font-mono" data-testid="corp-domain" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Billing email (for invoices)</Label><Input type="email" value={f.billing_email} onChange={(e) => setF({ ...f, billing_email: e.target.value })} placeholder="accounts@company.co.nz" className="bg-black/30 border-white/10 mt-1 h-10" data-testid="corp-billing-email" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Self-serve password (optional)</Label><Input type="password" value={f.admin_password} onChange={(e) => setF({ ...f, admin_password: e.target.value })} placeholder="Min 8 chars — creates login for billing email" className="bg-black/30 border-white/10 mt-1 h-10" data-testid="corp-admin-password" /></div>
              <div><Label className="text-xs uppercase text-slate-400">Monthly limit (NZ$)</Label><Input type="number" value={f.monthly_limit_nzd} onChange={(e) => setF({ ...f, monthly_limit_nzd: e.target.value })} className="bg-black/30 border-white/10 mt-1 h-10" data-testid="corp-monthly-limit" /></div>
              <Button type="submit" className="btn-gold" data-testid="corp-submit">Create</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {corps.length === 0 && <div className="col-span-2 glass-card rounded-2xl p-8 text-center text-slate-500 gold-border">No corporate accounts yet.</div>}
        {corps.map((c) => (
          <div key={c.id} className="glass-card rounded-2xl p-5 gold-border" data-testid={`corp-card-${c.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display font-bold text-lg">{c.name}</div>
                <div className="font-mono text-xs text-amber-400 mt-1">@{c.email_domain}</div>
              </div>
              <Badge className={`${c.active ? "bg-emerald-400/20 text-emerald-300 border-emerald-400/40" : "bg-slate-400/20 text-slate-400 border-slate-400/30"} border font-mono uppercase text-[10px] tracking-wider`}>{c.active ? "Active" : "Inactive"}</Badge>
            </div>
            <div className="mt-4 pt-4 border-t border-white/5 text-sm space-y-1">
              <div className="text-slate-400">Monthly cap: <span className="font-mono text-white">NZ$ {Number(c.monthly_limit_nzd).toFixed(2)}</span></div>
              {c.billing_email && <div className="text-slate-400">Bills to: <span className="font-mono text-amber-400 text-xs">{c.billing_email}</span></div>}
            </div>
            <div className="mt-4 flex gap-2">
              <Button variant="outline" size="sm" onClick={() => previewInvoice(c.id)} disabled={previewLoading === c.id} className="bg-transparent border-amber-400/40 text-amber-400 hover:bg-amber-400/10 flex-1" data-testid={`corp-preview-pdf-${c.id}`}><Eye className="w-3.5 h-3.5 mr-2" /> {previewLoading === c.id ? "Loading…" : "Preview PDF"}</Button>
              <Button size="sm" onClick={() => sendInvoice(c.id)} disabled={sendingId === c.id} className="btn-gold flex-1" data-testid={`corp-send-invoice-${c.id}`}><Send className="w-3.5 h-3.5 mr-2" /> {sendingId === c.id ? "Sending…" : "Email PDF"}</Button>
              <Button variant="outline" size="icon" onClick={() => delCorp(c.id)} className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 h-9 w-9"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!previewUrl} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-4xl">
          <DialogHeader><DialogTitle className="font-display">Invoice preview</DialogTitle></DialogHeader>
          {previewUrl && <iframe src={previewUrl} title="Invoice PDF preview" className="w-full h-[70vh] rounded-lg bg-white" data-testid="invoice-preview-iframe" />}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={closePreview} className="bg-transparent border-white/15 hover:bg-white/5 text-white">Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!invoice} onOpenChange={() => setInvoice(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">Invoice — {invoice?.corporate?.name}</DialogTitle></DialogHeader>
          {invoice && (
            <div>
              <div className="text-sm text-slate-400 mb-4">{invoice.rider_count} rider(s) · {invoice.trips.length} completed trip(s)</div>
              <div className="max-h-80 overflow-y-auto space-y-2">
                {invoice.trips.map((t) => (
                  <div key={t.id} className="flex justify-between items-center border-b border-white/5 py-2 text-sm">
                    <div><div className="font-mono text-amber-400">#{t.reference}</div><div className="text-xs text-slate-500">{t.rider_name}</div></div>
                    <div className="font-mono text-white">NZ$ {t.fare_estimate.toFixed(2)}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-white/10 flex justify-between text-lg font-display font-bold">
                <span>Total due</span><span className="text-amber-400">NZ$ {invoice.total_nzd.toFixed(2)}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
